import { useEffect, useMemo, useState, Fragment } from 'react';
import { toast } from 'sonner';
import { Desktop, ArrowRight, CheckCircle, CaretDown, CaretRight } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import AssetFormDialog from '@/pages/it/AssetFormDialog';
import { itApi } from '@/pages/it/itApi';
import { bihinApi } from '@/pages/bihin/bihinApi';
import { assetProcurementApi } from '@/lib/assetProcurementApi';
import { LocationPicker } from '@/components/registration';
import useDirectory from '@/hooks/useDirectory';
import { shizaiApi } from './shizaiApi';
import { todayJst } from '@/utils/jstDate';

/**
 * RegisterAsAssetDialog — 受入済みの明細を台帳（IT資産 / 備品）へ登録する橋渡し。
 *
 * 数量が2以上の設備は **1台=1行** で登録し、台ごとの 製造番号 をこの画面で
 * 入力する（空欄は「あとで入力」= `serial_pending`）。行の作成は各台帳の
 * 既存の登録APIをそのまま呼び、製造番号だけ共通の
 * `POST /api/asset-procurement/serials` で書く。
 *
 * 消耗品は台数の概念が無いので分割しない（数量1行のまま）。
 */
const CATEGORY_KEYWORDS = [
  ['PC・ノートPC',          ['ノート', 'ノートpc', 'notebook', 'laptop', 'デスクトップ', 'pc', 'workstation', 'ワークステーション']],
  ['モニター',              ['モニター', 'ディスプレイ', 'monitor', 'display']],
  ['スマートフォン・タブレット', ['スマホ', 'スマートフォン', 'iphone', 'android', 'ipad', 'tablet', 'タブレット']],
  ['プリンター・複合機',    ['プリンター', 'プリンタ', 'printer', '複合機', 'コピー機']],
  ['ネットワーク機器',      ['スイッチ', 'ルーター', 'アクセスポイント', 'switch', 'router', 'ap', 'wifi', 'wi-fi', 'アクセス']],
  ['サーバー',              ['サーバー', 'server', 'サーバ']],
  ['NAS・ストレージ',       ['nas', 'ストレージ', 'ssd', 'hdd']],
  ['周辺機器',              ['キーボード', 'マウス', 'ヘッドセット', 'usb', 'ドック', 'アダプタ', 'ケーブル']],
  ['ソフトウェア・ライセンス', ['office', 'ライセンス', 'ソフトウェア', 'サブスクリプション']],
];

function guessCategory(name = '', partNo = '') {
  const hay = `${name} ${partNo}`.toLowerCase();
  for (const [cat, kws] of CATEGORY_KEYWORDS) {
    if (kws.some((k) => hay.includes(k))) return cat;
  }
  return 'その他';
}

const LEDGER_META = {
  it: { label: 'IT資産', title: 'IT資産として登録' },
  office: { label: '備品', title: '備品として登録' },
};

export default function RegisterAsAssetDialog({ open, order, ledger = 'it', onClose, onRegistered }) {
  const [rows, setRows] = useState([]);
  const { employees } = useDirectory();
  const [saving, setSaving] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(null); // { line, prefill } — IT資産の詳細フォーム
  const meta = LEDGER_META[ledger] || LEDGER_META.it;

  useEffect(() => {
    if (!open || !order) return;
    const seeded = (order.lines || []).map((l, i) => {
      const qty = Math.max(1, Math.round(Number(l.qty || 1)));
      return {
        idx: i,
        selected: true,
        name: l.name || '',
        part_no: l.part_no || '',
        category: guessCategory(l.name, l.part_no),
        qty,
        unit_price: Number(l.unit_price || 0),
        amount: Number(l.amount ?? l.received_amount ?? 0),
        maker: l.maker || '',
        maker_pn: l.maker_pn || '',
        model_no: l.model_no || '',
        reference_url: l.reference_url || '',
        uom: l.uom || '個',
        // 1台ごとの製造番号・保管場所・使用者。空欄は「あとで入力」
        serials: Array.from({ length: qty }, () => ''),
        locations: Array.from({ length: qty }, () => ''),
        locationNames: Array.from({ length: qty }, () => ''),
        users: Array.from({ length: qty }, () => ''),
        expanded: qty > 1,
      };
    });
    setRows(seeded);
  }, [open, order]);

  const selectedCount = rows.filter((r) => r.selected).length;
  const unitCount = rows.filter((r) => r.selected).reduce((s, r) => s + r.qty, 0);

  const purchaseDate = useMemo(() => (
    (order?.received_at || order?.order_date || todayJst()).slice(0, 10)
  ), [order]);

  const supplier = order?.supplier?.name || order?.supplier_name || '';
  const orderNo = order?.order_no || order?.request_no || '';
  const patchRow = (idx, p) => setRows((prev) => prev.map((r) => (r.idx === idx ? { ...r, ...p } : r)));

  const unitCost = (row) => (row.qty > 0 && row.amount ? Math.round(row.amount / row.qty) : row.unit_price);

  const buildItPrefill = (row) => ({
    name: row.name,
    category: row.category,
    maker: row.maker,
    model: row.model_no,
    maker_pn: row.maker_pn,
    reference_url: row.reference_url,
    cost: unitCost(row),
    purchase_date: purchaseDate,
    vendor: supplier,
    supplier_name: supplier,
    notes: `発注: ${orderNo}${row.part_no ? ` / 品番: ${row.part_no}` : ''}`,
    status: 'in_stock',
    asset_type: 'durable',
    source_request_id: order?.id || null,
    source_request_no: orderNo || null,
    source_line_index: row.idx,
    auto_shared_no: true,
  });

  const buildOfficePayload = (row) => ({
    name: row.name,
    kind: 'equipment',
    category: null,
    status: 'storage',
    maker: row.maker || null,
    model_no: row.model_no || null,
    maker_pn: row.maker_pn || null,
    reference_url: row.reference_url || null,
    supplier_name: supplier || null,
    purchase_date: purchaseDate,
    price: unitCost(row) || null,
    unit: row.uom || '個',
    notes: `発注: ${orderNo}${row.part_no ? ` / 品番: ${row.part_no}` : ''}`,
  });

  const registerAll = async () => {
    const selected = rows.filter((r) => r.selected);
    if (selected.length === 0) { toast.error('登録する明細を選択してください'); return; }
    setSaving(true);
    let ok = 0;
    let err = 0;
    const serialEntries = [];
    const createdByLine = {};
    for (const row of selected) {
      for (let u = 0; u < row.qty; u += 1) {
        const suffix = row.qty > 1 ? ` (${u + 1}/${row.qty})` : '';
        try {
          const created = ledger === 'office'
            ? await bihinApi.createItem({
              ...buildOfficePayload(row), name: `${row.name}${suffix}`,
              location_id: row.locations?.[u] || null,
              location: row.locationNames?.[u] || null, auto_shared_no: true,
            })
            : await itApi.createAsset({
              ...buildItPrefill(row), name: `${row.name}${suffix}`,
              location_id: row.locations?.[u] || null,
              location: row.locationNames?.[u] || null,
              assigned_to_employee_id: row.users?.[u] || null,
            });
          ok += 1;
          createdByLine[row.idx] = [...(createdByLine[row.idx] || []), created?.id].filter(Boolean);
          if (created?.id) serialEntries.push({ ledger, id: created.id, serial: row.serials[u] || null });
        } catch (e) {
          err += 1;
          console.error('[bridge] register failed', row, e?.response?.data);
        }
      }
    }
    // C. どの明細が何台ぶん台帳に入ったかを依頼側に残す（二重登録が見えるように）
    if (order?.id) {
      for (const [idx, ids] of Object.entries(createdByLine)) {
        try {
          await shizaiApi.markAssetRegistration(order.id, {
            line_index: Number(idx), ledger, created_ids: ids, qty: ids.length,
          });
        } catch (e) { /* 台帳は作れているので登録自体は止めない */ }
      }
    }
    if (serialEntries.length) {
      try {
        await assetProcurementApi.saveSerials(serialEntries);
      } catch (e) {
        toast.warning('登録はできましたが製造番号の保存に失敗しました（台帳から入力できます）');
      }
    }
    setSaving(false);
    const pending = serialEntries.filter((e) => !e.serial).length;
    if (ok > 0 && err === 0) {
      toast.success(`${ok}件を${meta.label}に登録しました`,
        { description: pending ? `製造番号 未入力 ${pending}件（台帳から後で入力できます）` : undefined });
      onRegistered?.();
    } else if (ok > 0 && err > 0) {
      toast.warning(`${ok}件登録 / ${err}件失敗`);
    } else {
      toast.error('登録に失敗しました');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined} data-testid="register-asset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Desktop size={20} weight="duotone" className="text-indigo-600" />
              {meta.title}
              <span className="text-xs font-normal text-zinc-400">発注 {orderNo}</span>
            </DialogTitle>
          </DialogHeader>

          <p className="text-xs text-zinc-500 pb-2">
            受入済みの明細から{meta.label}を作成します。数量が2以上の明細は<b>1台ずつ別の行</b>として登録され、
            台ごとの製造番号・保管場所・使用者をここで入力できます（空欄のままでも登録でき、あとから台帳で入力できます）。品番は共通採番（EQP/CSM）から保存時に確定します。
          </p>

          <div className="rounded-lg border border-zinc-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-zinc-100 text-zinc-700">
                  <th className="w-10 px-2 py-2"></th>
                  <th className="text-left px-2 py-2">品名 / 品番</th>
                  {ledger === 'it' && <th className="text-left px-2 py-2 w-44">カテゴリ</th>}
                  <th className="text-right px-2 py-2 w-14">数量</th>
                  <th className="text-right px-2 py-2 w-20">単価</th>
                  <th className="text-center px-2 py-2 w-28">製造番号</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-zinc-400">明細がありません</td></tr>
                )}
                {rows.map((r) => (
                  <Fragment key={r.idx}>
                    <tr className="border-t border-zinc-200 align-top" data-testid={`register-asset-row-${r.idx}`}>
                      <td className="px-2 py-2">
                        <Checkbox
                          checked={r.selected}
                          onCheckedChange={(v) => patchRow(r.idx, { selected: !!v })}
                          data-testid={`register-asset-checkbox-${r.idx}`}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <div className="text-zinc-800">{r.name || '—'}</div>
                        {r.part_no && <div className="font-mono text-[10px] text-zinc-400">{r.part_no}</div>}
                      </td>
                      {ledger === 'it' && (
                        <td className="px-2 py-2">
                          <Select value={r.category} onValueChange={(v) => patchRow(r.idx, { category: v })}>
                            <SelectTrigger data-testid={`register-asset-category-${r.idx}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {CATEGORY_KEYWORDS.map(([cat]) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                              <SelectItem value="その他">その他</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      )}
                      <td className="px-2 py-2 text-right tabular-nums">{r.qty}</td>
                      <td className="px-2 py-2 text-right tabular-nums">¥{unitCost(r).toLocaleString('ja-JP')}</td>
                      <td className="px-2 py-2 text-center">
                        <button type="button" onClick={() => patchRow(r.idx, { expanded: !r.expanded })}
                          className="inline-flex items-center gap-1 text-[10px] text-indigo-600 hover:text-indigo-800"
                          data-testid={`register-asset-serial-toggle-${r.idx}`}>
                          {r.expanded ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
                          {r.qty}台を入力
                        </button>
                        {ledger === 'it' && (
                          <button type="button"
                            onClick={() => setAdvancedOpen({ line: r, prefill: buildItPrefill(r) })}
                            data-testid={`register-asset-advanced-${r.idx}`}
                            className="block w-full mt-1 text-[10px] text-zinc-500 hover:text-zinc-800 underline">
                            個別に登録
                          </button>
                        )}
                      </td>
                    </tr>
                    {r.expanded && r.selected && (
                      <tr className="bg-zinc-50/80">
                        <td />
                        <td colSpan={ledger === 'it' ? 5 : 4} className="px-2 pb-3">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2"
                            data-testid={`register-asset-serials-${r.idx}`}>
                            {r.serials.map((s, u) => (
                              <div key={u} className="rounded-md border border-zinc-200 bg-white p-1.5">
                                <span className="text-[10px] font-semibold text-zinc-500">{u + 1}台目</span>
                                <Input
                                  value={s}
                                  onChange={(e) => {
                                    const next = [...r.serials];
                                    next[u] = e.target.value;
                                    patchRow(r.idx, { serials: next });
                                  }}
                                  placeholder="製造番号（任意）"
                                  className="h-8 mt-1 text-[11px] font-mono"
                                  data-testid={`register-asset-serial-${r.idx}-${u}`}
                                />
                                <div className="mt-1" data-testid={`register-asset-location-${r.idx}-${u}`}>
                                  <LocationPicker value={r.locations?.[u] || ''}
                                    legacyText={r.locationNames?.[u] || ''}
                                    placeholder="保管場所"
                                    onChange={(id, row) => {
                                      const next = [...(r.locations || [])];
                                      const names = [...(r.locationNames || [])];
                                      next[u] = id || '';
                                      names[u] = (row && (row.path || row.name)) || '';
                                      patchRow(r.idx, { locations: next, locationNames: names });
                                    }}
                                    testId={`register-asset-location-picker-${r.idx}-${u}`} />
                                </div>
                                {ledger === 'it' && (
                                  <Select value={r.users?.[u] || '__none__'}
                                    onValueChange={(v) => {
                                      const next = [...(r.users || [])];
                                      next[u] = v === '__none__' ? '' : v;
                                      patchRow(r.idx, { users: next });
                                    }}>
                                    <SelectTrigger className="h-8 mt-1 text-[11px]"
                                      data-testid={`register-asset-user-${r.idx}-${u}`}>
                                      <SelectValue placeholder="使用者" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="__none__">使用者 未割当</SelectItem>
                                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-zinc-500 flex items-center gap-3 pt-2 flex-wrap">
            <span>取得日: <b className="text-zinc-700">{purchaseDate}</b></span>
            <span>仕入先: <b className="text-zinc-700">{supplier || '—'}</b></span>
            <span className="text-zinc-400" data-testid="register-asset-unit-count">
              選択中: {selectedCount}明細 / {unitCount}台
            </span>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose} data-testid="register-asset-cancel">
              {ledger === 'it' ? 'キャンセル' : 'あとで登録する'}
            </Button>
            <Button onClick={registerAll} disabled={saving || selectedCount === 0}
              data-testid="register-asset-submit"
              className="bg-teal-700 hover:bg-teal-800">
              <CheckCircle size={15} weight="fill" className="mr-1.5" />
              {saving ? '登録中...' : `${unitCount}台を登録`}
              <ArrowRight size={14} weight="bold" className="ml-1.5" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 個別に登録 — IT資産の詳細フォームをプレフィルで開く */}
      {advancedOpen && (
        <AssetFormDialog
          open={!!advancedOpen}
          onClose={() => setAdvancedOpen(null)}
          prefill={advancedOpen.prefill}
          onSaved={() => {
            setAdvancedOpen(null);
            toast.success('資産を登録しました（残りの明細は引き続き選択できます）');
          }}
        />
      )}
    </>
  );
}
